import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, CreditCard, Wallet } from 'lucide-react';
import {
  calculateTicketPricing,
  formatCurrency,
  formatDuration,
  formatHourlyRate,
  formatNamibianDateTime
} from '../../utils/parkingTicketUtils';
import PayPointBranding from '../common/PayPointBranding';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #7f1b1c 0%, #640003 100%);
  padding: 120px 20px 80px;
`;

const Card = styled.div`
  max-width: 720px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #64748b;
`;

const Summary = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  color: #334155;
  font-size: 0.95rem;

  strong {
    color: #1e293b;
  }
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MethodButton = styled.button`
  border: 2px solid ${(props) => (props.$primary ? '#c31321' : '#e2e8f0')};
  border-radius: 14px;
  background: ${(props) => (props.$primary
    ? 'linear-gradient(150deg, rgba(195, 19, 33, 0.08), rgba(255, 255, 255, 1))'
    : '#fff')};
  padding: 20px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #c31321;
    transform: translateY(-2px);
  }
`;

const MethodTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1e293b;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 8px;
`;

const MethodText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PaymentMethodSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ticket = location.state?.ticket;
  const plateKey = location.state?.plateKey || '';
  const pricing = ticket ? calculateTicketPricing(ticket.attributes || {}, plateKey) : null;

  if (!ticket || !pricing) {
    return (
      <PageContainer>
        <Card>
          <Title>Payment unavailable</Title>
          <Subtitle>Please select a ticket from Open Tickets before continuing to payment.</Subtitle>
          <BackButton type="button" onClick={() => navigate('/smart-parking/open-tickets')}>
            <ArrowLeft size={16} />
            Back to Open Tickets
          </BackButton>
        </Card>
      </PageContainer>
    );
  }

  const paymentContext = {
    ticket,
    pricing,
    plateKey,
    source: 'parking'
  };

  return (
    <PageContainer>
      <Card>
        <BackButton type="button" onClick={() => navigate('/smart-parking/open-tickets')}>
          <ArrowLeft size={16} />
          Back to Open Tickets
        </BackButton>

        <Title>Choose Payment Method</Title>
        <Subtitle>Select how you would like to pay for your KPI open parking ticket.</Subtitle>

        <Summary>
          <SummaryRow>
            <span>Vehicle plate</span>
            <strong>{pricing.vehiclePlate}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Parking slot</span>
            <strong>{pricing.parkingSlot}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Duration</span>
            <strong>{formatDuration(pricing.billableHours)}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Rate</span>
            <strong>{formatHourlyRate(pricing.hourlyRate, pricing.currency)}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Stay</span>
            <strong>
              {formatNamibianDateTime(pricing.startDate)} – {formatNamibianDateTime(pricing.endDate)}
            </strong>
          </SummaryRow>
          <SummaryRow>
            <span>Total payable</span>
            <strong>{formatCurrency(pricing.totalPayable, pricing.currency)}</strong>
          </SummaryRow>
        </Summary>

        <MethodGrid>
          <MethodButton
            type="button"
            $primary
            onClick={() => navigate('/kp-card/recharge', { state: { parkingPayment: paymentContext } })}
          >
            <MethodTitle>
              <Wallet size={20} color="#c31321" />
              KP Card
            </MethodTitle>
            <MethodText>
              Pay with your virtual or physical KP Card using the existing card payment page.
            </MethodText>
          </MethodButton>

          <MethodButton
            type="button"
            onClick={() => navigate('/smart-parking/pay/card', { state: { parkingPayment: paymentContext } })}
          >
            <MethodTitle>
              <CreditCard size={20} color="#1d4ed8" />
              Debit / Credit Card
            </MethodTitle>
            <MethodText>
              Pay securely online with your debit or credit card.
            </MethodText>
          </MethodButton>
        </MethodGrid>

        <PayPointBranding />
      </Card>
    </PageContainer>
  );
};

export default PaymentMethodSelect;
